import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { WeekSchedule } from '@/components/business/week-schedule'
import { NumericInputFormField } from '@/components/form/numeric-input-form-field'
import { SelectFormField } from '@/components/form/select-form-field'
import { SwitchFormField } from '@/components/form/switch-form-field'
import { Alert } from '@/components/ui/alert'
import { Divider } from '@/components/ui/divider'

import type { PackagesSettingsForm } from '../packages-settings.types'
import {
  getAsapTip,
  getD2DLockedMessage,
  getD2DTip,
  getQuickScanLockedMessage,
  getSlotLengthOptions,
  getWeekScheduleLabels,
} from './general-tab.constants'
import * as S from './general-tab.styled'

type GeneralTabProps = {
  isD2DAvailable: boolean
  isQuickScanAvailable: boolean
}

export const GeneralTab = ({ isD2DAvailable, isQuickScanAvailable }: GeneralTabProps) => {
  const { t } = useTranslation(['packages-settings'])
  const d2dMode = useWatch<PackagesSettingsForm, 'd2dMode'>({ name: 'd2dMode' })
  const allowASAP = useWatch<PackagesSettingsForm, 'allowASAP'>({ name: 'allowASAP' })
  const allowQuickScan = useWatch<PackagesSettingsForm, 'allowQuickScan'>({
    name: 'allowQuickScan',
  })
  const trackingNumberRequired = useWatch<PackagesSettingsForm, 'trackingNumberRequired'>({
    name: 'trackingNumberRequired',
  })
  const photoRequired = useWatch<PackagesSettingsForm, 'photoRequired'>({ name: 'photoRequired' })

  return (
    <S.Container>
      <SwitchFormField
        name="d2dMode"
        label={t('packages-settings:generalTab.d2dMode.label')}
        tooltip={isD2DAvailable ? getD2DTip(t) : undefined}
        disabled={!isD2DAvailable}
        inlineLabel={d2dMode ? t('packages-settings:common.yes') : t('packages-settings:common.no')}
      />

      {!isD2DAvailable && <Alert type="info" title={getD2DLockedMessage(t)} />}

      {d2dMode && isD2DAvailable && (
        <S.D2DSection>
          <SelectFormField
            name="deliverySlotLength"
            label={t('packages-settings:generalTab.deliverySlotLength.label')}
            options={getSlotLengthOptions(t)}
          />

          <WeekSchedule name="businessHours" labels={getWeekScheduleLabels(t)} />

          <NumericInputFormField
            name="maxDeliveriesPerSlot"
            label={t('packages-settings:generalTab.maxDeliveriesPerSlot.label')}
            mode="integer"
            min={1}
          />

          <div>
            <Divider color="gray500" />
            <SwitchFormField
              name="allowASAP"
              label={t('packages-settings:generalTab.allowASAP.label')}
              tooltip={getAsapTip(t)}
              inlineLabel={
                allowASAP ? t('packages-settings:common.yes') : t('packages-settings:common.no')
              }
            />
          </div>
        </S.D2DSection>
      )}

      <Divider color="gray500" />

      <SwitchFormField
        name="allowQuickScan"
        label={t('packages-settings:generalTab.allowQuickScan.label')}
        disabled={!isQuickScanAvailable}
        tooltip={t('packages-settings:generalTab.allowQuickScan.tip')}
        inlineLabel={
          allowQuickScan ? t('packages-settings:common.yes') : t('packages-settings:common.no')
        }
      />

      {!isQuickScanAvailable && <Alert type="info" title={getQuickScanLockedMessage(t)} />}

      <Divider color="gray500" />

      <SwitchFormField
        name="trackingNumberRequired"
        label={t('packages-settings:generalTab.trackingNumberRequired.label')}
        tooltip={t('packages-settings:generalTab.trackingNumberRequired.tip')}
        inlineLabel={
          trackingNumberRequired
            ? t('packages-settings:common.yes')
            : t('packages-settings:common.no')
        }
      />

      <Divider color="gray500" />

      <SwitchFormField
        name="photoRequired"
        label={t('packages-settings:generalTab.photoRequired.label')}
        tooltip={t('packages-settings:generalTab.photoRequired.tip')}
        inlineLabel={
          photoRequired ? t('packages-settings:common.yes') : t('packages-settings:common.no')
        }
      />
    </S.Container>
  )
}
